import {
  ChevronLeft,
  ChevronRight,
  Pause,
  Play,
  RotateCcw,
  EyeOff,
  Eye,
} from "lucide-react";
import { SLIDES } from "@/data/growthOSData";

/**
 * Bottom-center cinematic controls: progress dots, prev/next, pause toggle,
 * replay + hero-frame.
 */
export default function SlideControls({
  index,
  total,
  next,
  prev,
  goTo,
  paused,
  togglePause,
  staticFrame,
  toggleStatic,
  replay,
  isFirst,
  isLast,
}) {
  return (
    <div
      className="fixed bottom-4 sm:bottom-6 left-1/2 -translate-x-1/2 z-50 flex flex-col items-center gap-3"
      data-testid="slide-controls"
    >
      {/* slide progress dots */}
      <div className="flex items-center gap-1 max-w-[90vw] flex-wrap justify-center">
        {SLIDES.map((s, i) => (
          <button
            key={s.id}
            type="button"
            onClick={() => goTo(i)}
            className="group relative h-1.5 transition-all"
            style={{ width: i === index ? 20 : 7 }}
            data-testid={`slide-dot-${i + 1}`}
            aria-label={`Go to slide ${i + 1}`}
          >
            <span
              className={`absolute inset-0 rounded-full transition-all ${
                i === index
                  ? "bg-white shadow-[0_0_10px_rgba(255,255,255,0.7)]"
                  : i < index
                    ? "bg-white/55"
                    : "bg-white/15 group-hover:bg-white/35"
              }`}
            />
          </button>
        ))}
      </div>

      {/* control bar */}
      <div className="flex items-center gap-1.5 glass-strong rounded-full px-2 py-2">
        <button
          type="button"
          onClick={prev}
          disabled={isFirst}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[10px] sm:text-[11px] tracking-[0.18em] uppercase font-mono text-white/80 hover:text-white hover:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed transition"
          data-testid="prev-slide-button"
        >
          <ChevronLeft className="w-3.5 h-3.5" strokeWidth={1.8} />
          <span className="hidden sm:inline">Prev</span>
        </button>

        <button
          type="button"
          onClick={togglePause}
          className="px-2 py-1.5 rounded-full text-white/80 hover:text-white hover:bg-white/10 transition"
          data-testid="pause-button"
          aria-label={paused ? "Resume animation" : "Pause animation"}
          title={paused ? "Resume motion" : "Pause motion"}
        >
          {paused ? (
            <Play className="w-3.5 h-3.5" strokeWidth={1.8} />
          ) : (
            <Pause className="w-3.5 h-3.5" strokeWidth={1.8} />
          )}
        </button>

        <button
          type="button"
          onClick={replay}
          className="px-2 py-1.5 rounded-full text-white/80 hover:text-white hover:bg-white/10 transition"
          data-testid="replay-animation-button"
          title="Replay reveal"
        >
          <RotateCcw className="w-3.5 h-3.5" strokeWidth={1.8} />
        </button>

        <button
          type="button"
          onClick={toggleStatic}
          className="px-2 py-1.5 rounded-full text-white/80 hover:text-white hover:bg-white/10 transition"
          data-testid="toggle-static-frame-button"
          title={staticFrame ? "Resume loop" : "Hero frame"}
        >
          {staticFrame ? (
            <Eye className="w-3.5 h-3.5" strokeWidth={1.8} />
          ) : (
            <EyeOff className="w-3.5 h-3.5" strokeWidth={1.8} />
          )}
        </button>

        <div className="w-px h-5 bg-white/10 mx-1" />

        <div
          className="px-2 text-[10px] sm:text-[11px] tracking-[0.32em] font-mono text-white/55 select-none"
          data-testid="slide-indicator"
        >
          {String(index + 1).padStart(2, "0")}
          <span className="text-white/25"> / </span>
          {String(total).padStart(2, "0")}
        </div>

        <button
          type="button"
          onClick={next}
          disabled={isLast}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[10px] sm:text-[11px] tracking-[0.18em] uppercase font-mono text-white hover:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed transition"
          data-testid="next-slide-button"
        >
          <span className="hidden sm:inline">Next</span>
          <ChevronRight className="w-3.5 h-3.5" strokeWidth={1.8} />
        </button>
      </div>

      <div className="hidden sm:block text-[9px] tracking-[0.36em] font-mono uppercase text-white/30 select-none">
        ←  →  to navigate · space to advance · . to pause
      </div>
    </div>
  );
}
